import { getEnvironment, refreshAuthorizationToken } from '../environment/Environment';
import { axiosInstance } from '../axios/Axios';
import { HalIOConfig } from '../hal/HalIO';
import { trackException } from "../analytics/Analytics";

const HTTP_UNAUTHORIZED = '401';

export interface ErrorDetails {
   applicationId: string;
   buildNo?: string;
   timeStamp: string;
   errorCode?: string;
   message?: string;
   correlationId?: string;
   partnerCode?: string;
   databaseId?: string;
   propertyId?: string;
   requestMethod?: string;
   requestUrl?: string;
   userAgent?: string;
   errorText?: string;
   stackTrace?: string;
   comments?: string;
}

export type ErrorHandlerFunction = (errorDetails: ErrorDetails,
                                    onErrorAcknowledged?: () => void) => void;

/**
 * By default, function executes console log with error message. Function should be
 * overridden by initializeErrorHandler inside application using it, and when
 * error occurs it will be executed (window onerror).
 *
 * @param {ErrorDetails} errorDetails
 */
let globalErrorHandler: ErrorHandlerFunction = (errorDetails: ErrorDetails): void => {
   console.error(`ERROR: ${errorDetails.message}`);
};

const handleErrorImpl = (errorMessage: string,
                         errorDetails: ErrorDetails,
                         onErrorAcknowledged?: () => void): void => {
   trackException(errorMessage, false);

   if (errorDetails.errorCode === HTTP_UNAUTHORIZED) {
      refreshAuthorizationToken();
   }
   else {
      globalErrorHandler(errorDetails, onErrorAcknowledged);
   }
};

/**
 * Function sets globalErrorHandler with passed function.
 *
 * @param {(errorDetails: ErrorDetails, onErrorAcknowledged?: () => void) => void} errorHandler
 */
export const initializeErrorHandler = (errorHandler: (errorDetails: ErrorDetails,
                                                      onErrorAcknowledged?: () => void) => void): void => {
   globalErrorHandler = errorHandler;
};

/**
 * Function is setting window onerror where onerror calls globalErrorHandler with error details.
 */
export const installWindowOnErrorHandler = (): void => {

   window.onerror = (event: Event | string,
                     source?: string,
                     fileno?: number,
                     columnNumber?: number,
                     error?: Error) => {

      if (event.toString().indexOf('ResizeObserver') >= 0 && event.toString().indexOf('loop') >= 0)
      {
         // Ignore these:
         // ResizeObserver loop limit exceeded
         // and
         // ResizeObserver loop completed with undelivered notifications.
         return;
      }

      const environment = getEnvironment();
      const errorDetails: ErrorDetails = {
         applicationId: environment.applicationId,
         buildNo: environment.buildNo || 'unknown',
         timeStamp: new Date().toISOString(),
         message: event.toString(),
         errorText: `ERROR: ${source}:${fileno}:${columnNumber}`,
         stackTrace: error ? error.stack : '',
         userAgent: window.navigator.userAgent,
         partnerCode: environment.partnerCode,
         databaseId: environment.databaseId ? environment.databaseId.toString() : undefined,
         propertyId: environment.propertyId ? environment.propertyId.toString() : undefined
      };

      handleErrorImpl(event.toString(), errorDetails);
   };
};

/**
 * Function calls globalErrorHandler with arguments passed. It is used in
 * axios interceptor on request reject and it is called if developer has not
 * disabled error handling.
 *
 * @param {ErrorDetails} errorDetails
 * @param {() => void} onErrorAcknowledged
 */
export const handleError = (errorDetails: ErrorDetails, onErrorAcknowledged?: () => void): void => {
   handleErrorImpl(errorDetails.message || errorDetails.errorText || "",
                   errorDetails,
                   onErrorAcknowledged);
};

/**
 * Function is sending error details data to error report url if url exists.
 * If this request fails, it will not be handled by the system.
 *
 * @param {ErrorDetails} errorDetails
 */
export const sendErrorDetails = (errorDetails: ErrorDetails): void => {
   const errorReportUrl: string | undefined = getEnvironment().errorReportUrl;

   if (errorReportUrl) {
      const config: HalIOConfig = {
         disableErrorHandling: true
      };

      axiosInstance.post(errorReportUrl, errorDetails, config);
   }
   else {
      console.warn("The errorReportUrl is not set, unable to send error reports to UniFocus")
   }
};
